import { supabase } from '../client';
import type { Database } from '../generated';

export type OrgSettings = Database['core']['Tables']['org_settings']['Row'];

export interface OrgSettingsInput {
  poPrefix?: string | null;
  shopOrderPrefix?: string | null;
  /**
   * Which Sendblue line should win when both managed (PeptideClients-
   * minted) and BYO (customer-provided) connections exist. Default
   * 'managed'. Flip to 'byo' to keep using a self-supplied connection
   * even after PeptideClients provisions a subaccount. See migration
   * `20260612000000_sendblue_managed_subaccounts.sql` and
   * `core.fn_sms_resolve_sender` (v3) for routing semantics.
   */
  messagingLinePreference?: 'managed' | 'byo' | null;
}

/**
 * Read the per-org marketplace settings via the
 * `core.rpc_get_org_settings` security-definer RPC. The RPC seeds a
 * row with the platform defaults (`PO` / `SO`) if one is missing, so
 * the caller always gets back a row.
 *
 * Throws if the caller is not a member of the org (the RPC raises
 * `42501`).
 */
export async function getOrgSettings(orgId: string): Promise<OrgSettings> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_get_org_settings', { p_org_id: orgId });
  if (error) throw error;
  return data as unknown as OrgSettings;
}

/**
 * Upsert per-org marketplace settings. Admin/owner only.
 *
 * Pass `null` or an empty string to leave a field unchanged. Prefix
 * fields are uppercased + validated server-side by the regex
 * `^[A-Z0-9][A-Z0-9-]{0,15}$`. There is no "clear" path because
 * prefixes always have a default value (`PO` / `SO`).
 */
export async function upsertOrgSettings(
  orgId: string,
  patch: OrgSettingsInput,
): Promise<OrgSettings> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_upsert_org_settings', {
      p_org_id: orgId,
      p_po_prefix: patch.poPrefix ?? null,
      p_shop_order_prefix: patch.shopOrderPrefix ?? null,
      p_messaging_line_preference: patch.messagingLinePreference ?? null,
    });
  if (error) throw error;
  return data as unknown as OrgSettings;
}
